// Decimal <-> fraction <-> percent, exact for terminating decimals and with a
// best rational approximation (continued fractions) for long or repeating ones.

export type FractionValue = { num: number; den: number };

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) [x, y] = [y, x % y];
  return x || 1;
}

export function simplify(num: number, den: number): FractionValue {
  const sign = den < 0 ? -1 : 1;
  const g = gcd(num, den);
  return { num: (sign * num) / g, den: (sign * den) / g };
}

/** Parses "3/8", "1 3/8", "-2 1/2" or a whole number. */
export function parseFraction(input: string): FractionValue {
  const s = input.trim().replace(/\s+/g, ' ');
  const mixed = /^(-)?(\d+) (\d+)\/(\d+)$/.exec(s);
  if (mixed) {
    const den = Number(mixed[4]);
    if (!den) throw new Error('The denominator cannot be 0.');
    const num = Number(mixed[2]) * den + Number(mixed[3]);
    return simplify(mixed[1] ? -num : num, den);
  }
  const simple = /^(-?\d+)\/(-?\d+)$/.exec(s);
  if (simple) {
    if (!Number(simple[2])) throw new Error('The denominator cannot be 0.');
    return simplify(Number(simple[1]), Number(simple[2]));
  }
  if (/^-?\d+$/.test(s)) return { num: Number(s), den: 1 };
  throw new Error('Enter a fraction like 3/8 or 1 3/8.');
}

/**
 * Exact fraction for a decimal string with up to 9 decimal places; repeating
 * patterns ("0.333333", "0.1666") fall back to the closest fraction with a
 * denominator up to 1000 when that matches to 6 decimals.
 */
export function decimalToFraction(input: string): {
  exact: FractionValue;
  approx: FractionValue | null;
} {
  const s = input.trim().replace(/,/g, '');
  const m = /^(-)?(\d*)(?:\.(\d+))?$/.exec(s);
  if (!m || (!m[2] && !m[3])) throw new Error('Enter a decimal like 0.625.');
  const decimals = (m[3] ?? '').slice(0, 9);
  const den = 10 ** decimals.length;
  const num = Number(m[2] || '0') * den + Number(decimals || '0');
  const exact = simplify(m[1] ? -num : num, den);
  const approx = bestRational(Number(s), 1000);
  const close = approx.den < exact.den && Math.abs(approx.num / approx.den - Number(s)) < 5e-6;
  return { exact, approx: close ? approx : null };
}

export function bestRational(x: number, maxDen: number): FractionValue {
  const sign = x < 0 ? -1 : 1;
  let v = Math.abs(x);
  let [h0, h1, k0, k1] = [0, 1, 1, 0];
  for (let i = 0; i < 40; i += 1) {
    const a = Math.floor(v);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    [h0, h1, k0, k1] = [h1, h2, k1, k2];
    if (Math.abs(v - a) < 1e-12) break;
    v = 1 / (v - a);
  }
  return simplify(sign * h1, k1 || 1);
}

export function formatFraction({ num, den }: FractionValue): string {
  return den === 1 ? String(num) : `${num}/${den}`;
}

export function formatMixed({ num, den }: FractionValue): string | null {
  if (den === 1 || Math.abs(num) < den) return null;
  const whole = Math.trunc(num / den);
  const rest = Math.abs(num % den);
  return rest ? `${whole} ${rest}/${den}` : String(whole);
}

export function formatDecimal(value: number): string {
  return Number.isFinite(value) ? String(Number(value.toPrecision(12))) : '';
}
